import React, { useEffect, useRef, useState } from 'react';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { yaml } from '@codemirror/lang-yaml';
import { oneDark } from '@codemirror/theme-one-dark';
import yaml_parser from 'js-yaml';

function YamlEditor({ 
  value, 
  onChange, 
  placeholder = 'Enter YAML...', 
  height = '400px',
  onFormat = null,
  onValidate = null,
  readOnly = false 
}) {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [validationStatus, setValidationStatus] = useState('');

  // Check dark mode preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);
    
    const handler = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (editorRef.current && !viewRef.current) {
      const extensions = [
        basicSetup,
        yaml(),
        EditorView.updateListener.of((update) => {
          if (update.docChanged && onChange) {
            onChange(update.state.doc.toString());
          }
        }),
        EditorView.theme({
          '&': { 
            height: height,
            width: '100%'
          },
          '.cm-scroller': { 
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            scrollbarWidth: 'thin'
          },
          '.cm-focused': { outline: 'none' },
          '.cm-editor': { 
            borderRadius: '8px',
            width: '100%',
            boxSizing: 'border-box'
          },
          '.cm-content': { 
            padding: '1rem',
            minHeight: '100%'
          },
        }),
        EditorState.readOnly.of(readOnly),
      ];

      // Add dark theme if needed
      if (isDarkMode) {
        extensions.push(oneDark);
      }

      const state = EditorState.create({
        doc: value || '',
        extensions,
      });

      viewRef.current = new EditorView({
        state,
        parent: editorRef.current,
      });
    }

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [isDarkMode, height, readOnly]); // Re-create editor when these change

  // Update editor content when value prop changes
  useEffect(() => {
    if (viewRef.current && value !== viewRef.current.state.doc.toString()) {
      viewRef.current.dispatch({
        changes: {
          from: 0,
          to: viewRef.current.state.doc.length,
          insert: value || '',
        },
      });
    }
  }, [value]);

  const handleFormat = () => {
    if (viewRef.current) {
      try {
        const currentValue = viewRef.current.state.doc.toString();
        const parsed = yaml_parser.load(currentValue);
        const formatted = yaml_parser.dump(parsed, { 
          indent: 2,
          lineWidth: 80,
          noRefs: true,
          sortKeys: false
        });
        
        viewRef.current.dispatch({
          changes: {
            from: 0,
            to: viewRef.current.state.doc.length,
            insert: formatted,
          },
        });
        
        if (onFormat) {
          onFormat(formatted);
        }
      } catch (error) {
        console.error('YAML formatting error:', error);
      }
    }
  };

  const validateYaml = () => {
    if (viewRef.current) {
      try {
        const currentValue = viewRef.current.state.doc.toString();
        yaml_parser.load(currentValue);
        setValidationMessage('YAML is valid!');
        setValidationStatus('success');
        setTimeout(() => {
          setValidationMessage('');
          setValidationStatus('');
        }, 3000);
        if (onValidate) {
          onValidate({ valid: true, error: null });
        }
        return { valid: true, error: null };
      } catch (error) {
        setValidationMessage(`YAML Error: ${error.message}`);
        setValidationStatus('error');
        setTimeout(() => {
          setValidationMessage('');
          setValidationStatus('');
        }, 5000);
        if (onValidate) {
          onValidate({ valid: false, error: error.message });
        }
        return { valid: false, error: error.message };
      }
    }
    return { valid: true, error: null };
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast Message */}
      {validationMessage && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          fontSize: '0.875rem',
          fontWeight: '500',
          backgroundColor: validationStatus === 'success' ? '#d1fae5' : '#fee2e2',
          color: validationStatus === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${validationStatus === 'success' ? '#a7f3d0' : '#fecaca'}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          {validationStatus === 'success' ? '✅' : '❌'} {validationMessage}
        </div>
      )}
      
      <div 
        ref={editorRef}
        style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}
      />
      {!readOnly && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: '0.5rem',
          fontSize: '0.875rem',
          color: '#6b7280'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={handleFormat}
              className="btn btn-secondary"
              style={{ 
                fontSize: '0.75rem', 
                padding: '0.25rem 0.5rem',
                height: 'auto'
              }}
            >
              🎨 Format YAML
            </button>
            <button
              type="button"
              onClick={validateYaml}
              className="btn btn-secondary"
              style={{ 
                fontSize: '0.75rem', 
                padding: '0.25rem 0.5rem',
                height: 'auto'
              }}
            >
              ✅ Validate
            </button>
          </div>
          <div style={{ fontStyle: 'italic' }}>
            💡 Press Ctrl+Space for auto-complete
          </div>
        </div>
      )}
    </div>
  );
}

export default YamlEditor; 